import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  OrderApi,
  PaymentApi,
  OrderDTO,
  PaymentDTO,
  PaymentInvoiceDTO,
  UpdatePaymentInvoiceRequest,
  UpdatePaymentInvoiceRequestInvoiceStatusEnum,
} from '@lkp/parking-sdk';
import { ORDER_API, PAYMENT_API } from '../../core/providers/api.module';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { DateService } from 'src/app/core/services/date.service';
import { OrderService } from 'src/app/core/services/order.service';
import {
  GetOrder,
  ResetOrderState,
} from 'src/app/core/states/orders/orders.action';
import { OrdersState } from 'src/app/core/states/orders/orders.state';

@Component({
    selector: 'app-order',
    templateUrl: './order.component.html',
    styleUrls: ['./order.component.scss'],
    standalone: false
})
export class OrderComponent {
  @ViewChild('iframe', { read: ElementRef, static: false }) iframe:
    | ElementRef
    | undefined;

  @ViewChild('invoiceDialog') invoiceDialogTpl!: TemplateRef<any>;
  dialogRef?: MatDialogRef<any>;
  childFormValid = false;

  order?: OrderDTO;
  invoice?: PaymentInvoiceDTO;
  invoicePaymentId?: number;
  hasUnfinishedPayments = false;
  cancelledBookings = false;
  invoiceStatusForm: FormGroup = new FormBuilder().group({});

  /* email */
  emailSent = false;
  emailPaymentMsg?: string;
  emailRefundMsg?: string;
  emailConfirmationMsg?: string;
  emailConfirmationSent = false;
  loadingPaymentEmail = false;
  loadingRefundEmail = false;
  loadingConfirmationEmail = false;

  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly router: Router,
    readonly store: Store,
    readonly dateService: DateService,
    @Inject(ORDER_API) readonly orderApi: OrderApi,
    @Inject(PAYMENT_API) readonly paymentApi: PaymentApi,
    readonly cdr: ChangeDetectorRef,
    readonly dialog: MatDialog,
    readonly orderService: OrderService // readonly fb: FormBuilder
  ) {
    /* Only reset order state if navigation to something other than checkout or order */
    router.events.pipe(takeUntil(this.onDestroy$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (
          !event.url.includes('hantera-betalning') &&
          !event.url.includes('order')
        ) {
          this.store.dispatch(new ResetOrderState());
        }
      }
    });
  }

  ngOnInit() {
    if (this.order === undefined) {
      const id = this.router.url.split('/')[2];
      this.getOrder(+id);
    } else this.getOrder();

    this.store
      .select(OrdersState.order)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((order) => {
        this.order = order;

        const paymentWithInvoice = order?.payments?.find(
          (p) => p.paymentInvoice != null
        );

        // Store invoice and paymentId
        this.invoice = paymentWithInvoice?.paymentInvoice;
        this.invoicePaymentId = paymentWithInvoice?.paymentId;

        this.cancelledBookings = this.order?.orderItems
          ? this.orderService.allOrderBookingsCancelled(this.order.orderItems)
          : false;

        this.setFormControls();
      });
  }

  handleInvoiceStatusChange(paymentId: number, invoiceId: number, value: any) {
    if (value != null) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: `Är du säker på att du vill ändra status på fakturan till ${
            value ? 'fakturerad' : 'ej fakturerad'
          }?`,
          message: [
            `Att ändra fakturans status till ${
              value ? 'fakturerad' : 'ej fakturerad'
            } innebär att den kommer presenteras som ${
              value ? 'fakturerad' : 'ej fakturerad'
            } i systemet.`,
          ],
          yesBtnText: 'Ja',
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((res) => {
          if (res) {
            const invoiceStatus: UpdatePaymentInvoiceRequest = {
              paymentInvoiceId: invoiceId,
              invoiceStatus: value
                ? UpdatePaymentInvoiceRequestInvoiceStatusEnum.Handled
                : UpdatePaymentInvoiceRequestInvoiceStatusEnum.Unhandled,
            };

            this.paymentApi
              .updateInvoiceStatusAdmin({
                paymentId,
                updatePaymentInvoiceRequest: invoiceStatus,
              })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe(() => {
                this.getOrder();
              });
          } else {
            /* Update invoice status from current order */
            this.setFormControls();
          }
        });
    }
  }

  ngOnChanges() {
    this.cancelledBookings = this.order?.orderItems
      ? this.orderService.allOrderBookingsCancelled(this.order.orderItems)
      : false;

    this.setFormControls();
  }

  setFormControls(): void {
    if (this.order) {
      this.order?.payments?.forEach((payment) => {
        if (payment.paymentMethod?.paymentMethodId === 'INVOICE') {
          if (payment.paymentId) {
            if (
              !this.invoiceStatusForm.controls[payment.paymentId.toString()]
            ) {
              this.addInvoiceFormControl(
                payment.paymentId,
                payment.paymentInvoice?.paymentInvoiceId,
                {
                  value: payment.paymentInvoice?.invoiceStatus === 'HANDLED',
                  id: payment.paymentInvoice?.paymentInvoiceId,
                }
              );
            } else {
              this.invoiceStatusForm.controls[
                payment.paymentId.toString()
              ].setValue(payment.paymentInvoice?.invoiceStatus === 'HANDLED', {
                emitEvent: false,
              });
            }
          }
        }
      });
    }
  }

  addInvoiceFormControl(
    name: number,
    invoiceId: number | undefined,
    value: {
      value: boolean;
      id: number | undefined;
    }
  ): void {
    if (this.invoiceStatusForm) {
      this.invoiceStatusForm.addControl(
        name.toString(),
        new FormControl<boolean>(value.value)
      );

      this.invoiceStatusForm.controls[name.toString()].valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => {
          invoiceId && this.handleInvoiceStatusChange(name, invoiceId, value);
        });
    }
  }

  openInvoicePopup(): void {
    this.dialogRef = this.dialog.open(this.invoiceDialogTpl, {
      width: '900px',
    });
  }

  closeInvoiceDialog(): void {
    this.dialogRef?.close();
  }

  onInvoiceSaved(result: PaymentInvoiceDTO): void {
    const paymentId = this.invoicePaymentId;
    if (!paymentId) {
      console.error(
        '[Order] No INVOICE paymentId found; cannot update invoice.'
      );
      return;
    }

    const invoiceUpdate: PaymentInvoiceDTO = {
      ...(this.invoice ?? {}),
      ...result,
    };

    this.paymentApi
      .updatePaymentInvoiceAdmin({
        paymentId,
        paymentInvoiceDTO: invoiceUpdate,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (updated) => {
          this.invoice = updated;
          this.dialogRef?.close();
          this.getOrder();
        },
        error: (err) => console.error('[Order] Update failed:', err),
      });
  }

  getOrder(urlId?: number) {
    /* Need to get id from URL because order is reset after leaving order page */
    let id = urlId || this.order?.orderId;
    id !== undefined &&
      this.store
        .dispatch(new GetOrder(id))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          id !== undefined && // TS does not remember that this is not undefined
            this.orderApi
              .getUnfinishedPayments({ orderId: id })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe((response: PaymentDTO[]) => {
                this.hasUnfinishedPayments = response.length > 0;
              });
        });
  }

  formatDate(date?: Date | string): Date | string | undefined {
    return date && this.dateService.formatDate(date);
  }

  formatTime(date?: Date | string): Date | string | undefined {
    return date && this.dateService.formatTime(date);
  }

  awaitReceiptChanges(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.cdr.detectChanges();
      setTimeout(() => {
        resolve();
      }, 0);
    });
  }

  printReceipt(paymentId?: number) {
    paymentId &&
      this.paymentApi
        .getPaymentReceiptHtmlAdmin({ paymentId })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (v) => {
            if (v.html) {
              if (this.iframe) {
                this.iframe.nativeElement.contentWindow.document.open();
                this.iframe.nativeElement.contentWindow.document.close();
                this.iframe.nativeElement.contentWindow.frames.document.body.insertAdjacentHTML(
                  'afterbegin',
                  v.html
                );
                this.awaitReceiptChanges().then(() => {
                  this.iframe?.nativeElement.contentWindow.print();
                });
              }
            }
          },
          error: (error) => {
            console.error('Error fetching receipt: ', error);

            this.dialog.open(ErrorDialogComponent, {
              data: {
                title: 'Det gick inte att skriva ut kvittot',
                message: ['Kvittot kunde inte hittas i kassasystemet.'],
              },
            });
          },
        });
  }

  goToCheckout() {
    this.router.navigate(['hantera-betalning']);
  }

  hasPaymentType(type: 'KLARNA' | 'INVOICE'): boolean {
    return this.order?.payments
      ? this.order.payments?.some(
          (payment) => payment.paymentMethod?.paymentMethodId === type
        )
      : false;
  }

  sendKlarnaReceipt(payment?: PaymentDTO) {
    const confDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Är du säker på att du vill skicka en Klarna-kvittokopia till kunden?`,
        message: [
          `Kunden kommer att få en Klarna-kvittokopia till den angivna e-postadressen i bokningen.`,
        ],
        yesBtnText: 'Skicka',
      },
    });

    confDialog
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.emailSent = true;

          /* Set correct loading icon */
          if (payment && payment.transactionType === 'PAYMENT') {
            this.loadingPaymentEmail = true;
            this.loadingRefundEmail = false;
          } else if (payment) {
            this.loadingPaymentEmail = false;
            this.loadingRefundEmail = true;
          }

          /* Finished loading */
          this.emailPaymentMsg = undefined;
          this.emailRefundMsg = undefined;

          payment?.paymentId &&
            this.paymentApi
              .sendPaymentReceiptAdmin({ paymentId: payment.paymentId })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: () => {
                  this.emailSent = true;

                  /* Set correct email message */
                  if (payment.transactionType === 'PAYMENT') {
                    this.emailPaymentMsg = 'Kvitto skickat!';
                    this.emailRefundMsg = undefined;
                  } else {
                    this.emailPaymentMsg = undefined;
                    this.emailRefundMsg = 'Kvitto skickat!';
                  }

                  /* Finished loading */
                  this.loadingPaymentEmail = false;
                  this.loadingRefundEmail = false;
                },
                error: () => {
                  this.emailSent = false;

                  /* Set correct email message */
                  if (payment.transactionType === 'PAYMENT') {
                    this.emailPaymentMsg =
                      'Det gick inte att skicka mejlet, kontrollera e-postadressen.';
                    this.emailRefundMsg = undefined;
                  } else {
                    this.emailPaymentMsg = undefined;
                    this.emailRefundMsg =
                      'Det gick inte att skicka mejlet, kontrollera e-postadressen.';
                  }

                  /* Finished loading */
                  this.loadingPaymentEmail = false;
                  this.loadingRefundEmail = false;
                },
              });
        }
      });
  }

  sendConfirmationEmail() {
    const name = this.order?.orderItems?.find(
      (item) => item.orderItemType === 'RESOURCE'
    )?.booking?.name;

    const confDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Är du säker på att du vill skicka orderbekräftelse till ${
          name || 'okänd'
        }?`,
        message: [
          `Kunden kommer att få en orderbekräftelse till den angivna e-postadressen.`,
        ],
        yesBtnText: 'Skicka',
      },
    });

    confDialog
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          if (this.order?.orderId) {
            this.loadingConfirmationEmail = true;
            this.emailConfirmationMsg = undefined;

            this.orderApi
              .sendOrderConfirmationAdmin({ orderId: this.order?.orderId! })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: () => {
                  this.emailConfirmationSent = true;
                  this.emailConfirmationMsg = 'Orderbekräftelse skickad!';
                  this.loadingConfirmationEmail = false;
                },
                error: () => {
                  this.emailConfirmationSent = false;
                  this.emailConfirmationMsg =
                    'Det gick inte att skicka orderbekräftelsen, kontrollera e-postadressen.';
                  this.loadingConfirmationEmail = false;
                },
              });
          } else {
            this.emailConfirmationSent = false;
            this.emailConfirmationMsg =
              'Det gick inte att skicka orderbekräftelsen, ladda om sidan och försök igen.';
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
